import React, { useRef, type KeyboardEvent, type ReactNode } from 'react';

import * as styles from './Modal.css';
import FocusLock from 'react-focus-lock';
import {
  IconClear,
  Box,
  ButtonIcon,
  useResponsiveValue,
} from 'braid-design-system';
import { ESCAPE_KEY } from 'src/utils/buildHandleEnterAndSpaceKeyPress';
import useEventListener from 'src/utils/hooks/useEventListener';
import { useTranslations } from '@vocab/react';
import translations from './.vocab';

interface ModalProps {
  titleId: string;
  isOpen: boolean;
  close: () => void;
  children: ReactNode;
  width?: {
    mobile: string | null;
    desktop: string | null;
    tablet: string | null;
    wide: string | null;
  };
}

export const Modal = ({
  isOpen,
  close,
  children,
  titleId,
  width,
}: ModalProps) => {
  const { t } = useTranslations(translations);
  const content = useRef<HTMLDivElement>(null);
  const responsiveValue = useResponsiveValue();

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!isOpen) {
      return;
    }

    if (event.key === ESCAPE_KEY) {
      event.stopPropagation();
      close();
    }
  };

  useEventListener('keyDown', handleKeyDown);

  if (!isOpen) {
    return null;
  }

  const clickToClose = (e: { target: any }) => {
    if (!content || !content.current) {
      return;
    }
    const overlayOnly = !content.current.contains(e.target);

    if (overlayOnly) {
      close();
    }
  };

  const responsiveValueWidth = width ? responsiveValue(width) : null;

  return (
    <Box
      className={styles.overlay}
      onMouseDown={clickToClose}
      zIndex="modal"
      role="dialog"
      aria-labelledby={titleId}
      data={{
        testid: `modal-overlay-${titleId}`,
      }}
      onKeyDown={handleKeyDown}
    >
      <Box
        ref={content}
        className={styles.contentStyle}
        borderRadius="large"
        margin={{ mobile: 'small', tablet: 'none' }}
        {...(responsiveValueWidth && {
          style: { width: responsiveValueWidth },
        })}
      >
        <FocusLock>
          <Box
            position="absolute"
            display="flex"
            alignItems="center"
            justifyContent="center"
            top={0}
            right={0}
            marginTop="medium"
            marginRight="medium"
            zIndex={1}
          >
            <ButtonIcon
              icon={<IconClear />}
              label={t('Close')}
              id={`close-modal-${titleId}`}
              size="standard"
              onClick={close}
            />
          </Box>

          {children}
        </FocusLock>
      </Box>
    </Box>
  );
};
